import axiosInstance from "../../axiosInstance";
import { Invoice } from "../billingService/billingService";

type BillingInvoices = {
  invoice_id: number;
  invoice_name: string;
  due_date: string;
  total_amount: number;
  status: string;
  period_closed: boolean;
  pdf_url: string;
};

export type BillingInvoiceUpdate = {
  invoice_id: string | number | undefined;
  pdf_url: string | File;
  invoice_name?: string;
  due_date?: string;
  total_amount?: number;
  status?: string;
  period_closed?: boolean;
};

export interface ApartmentInfo {
  total_pages: number;
  current_page: number;
  all_apartments: string | null;
  apartments: ApartmentInfoResult[];
};

export interface ApartmentInfoResult {
  address: string;
  apartment_number: number;
  users: {
    smartId: string | null;
    eparaksId: string | null;
    user_type: "owner" | "renter";
    company_name: string | null;
    phone: string | null;
    email: string | null;
    telegram: string | null;
    whatsapp: string | null;
    info: string | null;
    user_name: string | null;
  }[];
};

export interface GetRepairWorksParams {
  building_id?: string;
  page?: number;
  page_size?: number;
  year?: string;
  status?: string;
}

export interface GetRepairWorksResponse {
  results: RepairWork[]
  pagination: {
    total_pages: number;
    current_page: number;
    total_items: number;
    page_size: number;
  }
  status_counts: {
    planned: number;
    in_progress: number;
    done: number;
    canceled: number;
  }
}

export interface RepairWork {
  id: number;
  title: string;
  description: string;
  building: number;
  status: string;
  created_at: string;
  files: {
    id: number;
    pdf: string;
  }[];
}

export interface RepairWorkPost {
  title: string;
  description: string;
  building: number;
  status: string;
  new_files: File[];
}
const HouseManagerApi = {
  postGenerateInvoices: async (
    billing_month: string,
    building_id?: string,
  ): Promise<Invoice[]> => {
    try {
      const response = await axiosInstance.post<Invoice[]>(
        "/api/billing/generate-invoices/",
        { billing_month, building_id }
      );

      return response.data;
    } catch (error) {
      console.error("Error while fetching bills:", error);
      throw error;
    }
  },

  postClosePeriod: async (
    building_id?: string
  ): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/billing/close-period/",
        { building_id }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching bills:", error);
      throw error;
    }
  },

  billingInvoices: async (
    updates: BillingInvoiceUpdate[],
  ): Promise<BillingInvoices[]> => {
    try {

      const formData = new FormData();

      const safeUpdates = updates.map(({ pdf_url, ...rest }, idx) => ({
        ...rest,
        pdf_url: pdf_url instanceof File ? idx : undefined,
      }));
      formData.append("updates", JSON.stringify(safeUpdates));

      updates.forEach((item) => {
        if (item.pdf_url instanceof File) {
          formData.append(`${item.invoice_id}`, item.pdf_url);
        }
      });



      const response = await axiosInstance.post(
        "/api/billing/invoices/",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error while updating bills:", error);
      throw error;
    }
  },

  getApartmentInfo: async (building_id: string, page = 1, page_size = 10): Promise<ApartmentInfo> => {
    try {
      const response = await axiosInstance.get<ApartmentInfo>(
        "/api/user/building-apartment-info",
        {
          params: {
            building_id: building_id,
            page: page,
            page_size: page_size,
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching apartment info:", error);
      throw error;
    }
  },

  postInvoiceDueDate: async (building_id: string | undefined, due_date: string | undefined): Promise<{ message: string }> => {
    try {
      const response = await axiosInstance.post<{ message: string }>(
        "/api/billing/invoice-due-date/",
        { building_id, due_date }
      );
      return response.data;
    } catch (error) {
      console.error("Error while updating invoice due date:", error);
      throw error;
    }
  },

  getRepairWorks: async (params?: GetRepairWorksParams): Promise<GetRepairWorksResponse> => {    
    try {
      const response = await axiosInstance.get<GetRepairWorksResponse>(
        "/api/location/repair-works/",
        { params }
      );
      return response.data;
    } catch (error) {
      console.error("Error while fetching repair works:", error);
      throw error;
    }
  },

  postRepairWorks: async (data: RepairWorkPost): Promise<RepairWork> => {
    const formData = new FormData();

    formData.append("title", data.title);
    formData.append("description", data.description);
    formData.append("building", String(data.building));
    formData.append("status", data.status);

    data.new_files?.forEach((file: File) => {
      if (file.size > 0) {
        formData.append("new_files", file);
      }
    });
    try {
      const response = await axiosInstance.post<RepairWork>(
        "/api/location/repair-works/",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while creating repair work:", error);
      throw error;
    }
  },

  patchRepairWorks: async (id: string, data: RepairWorkPost): Promise<RepairWork> => {
    const formData = new FormData();

    formData.append("title", data.title);
    formData.append("description", data.description);
    formData.append("building", String(data.building));
    formData.append("status", data.status);

    data.new_files?.forEach((file: File) => {
      if (file.size > 0) {
        formData.append("new_files", file);
      }
    });

    console.log('data.new_files', data.new_files)
    console.log('formDataPatch', formData)
    try {
      const response = await axiosInstance.patch<RepairWork>(
        `/api/location/repair-works/${id}/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error while updating repair work:", error);
      throw error;
    }
  },

  deleteRepairWorks: async (id: string): Promise<{ detail: string }> => {
    try {
      const response = await axiosInstance.delete<{ detail: string }>(
        `/api/location/repair-works/${id}/`
      );
      return response.data;
    } catch (error) {
      console.error("Error while deleting repair work:", error);
      throw error;
    }
  },

  deleteRepairWorksFile: async (id: string, file_id: string): Promise<{ detail: string }> => {
    try {
      const response = await axiosInstance.delete<{ detail: string }>(
        `/api/location/repair-works/${id}/files/${file_id}/`
      );
      return response.data;
    } catch (error) {
      console.error("Error while deleting repair work file:", error);
      throw error;
    }
  }
};

export default HouseManagerApi;
